import { createReducer, on } from '@ngrx/store';
import { routerNavigatedAction } from '@ngrx/router-store';

import * as GlobalActions from '@fcom/core/actions/global.actions';
import { updateStoreAction } from '@fcom/core/store/init-store.action';
import { DataUtils, getIn, setIn } from '@fcom/core/utils/data-utils';
import { equals } from '@fcom/core/utils/equals';
import { isPresent, unique, uniqueBy } from '@fcom/core/utils/utils';
import {
  CustomServiceType,
  ServiceCatalog,
  ServiceSelections,
  ServicesState,
  ServicesStatus,
  TravelerService,
} from '@fcom/dapi';
import { Category } from '@fcom/dapi/api/models/category';
import { FinnairPassengerServiceItem } from '@fcom/dapi/api/models/finnair-passenger-service-item';
import { FinnairServiceBoundItem } from '@fcom/dapi/api/models/finnair-service-bound-item';
import { SubCategory } from '@fcom/dapi/api/models/sub-category';
import * as GlobalBookingActions from '@fcom/common/store/actions/global-booking.actions';
import * as BookingActions from '@fcom/booking/store/actions/booking.actions';
import * as CartActions from '@fcom/booking/store/actions/cart.actions';
import * as OffersActions from '@fcom/booking/store/actions/offers.actions';
import { BookingAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { pathIsWithinHardcodedPath } from '@fcom/core/utils/app-url-utils';
import { RootPaths } from '@fcom/core/constants';
import { leaveError } from '@fcom/common/store/actions/error.actions';

import * as OrderActions from '../actions/order.actions';
import * as ServicesActions from '../actions/services.actions';
import { CartOrOrder } from '../../interfaces/cart-or-order.interface';
import { isInMmb, isNotIncludedService } from '../../utils/common-booking.utils';
import { asSegmentId, BOUND_FRAGMENT_PREFIX } from '../../utils/flight.utils';
import {
  asTravelerService,
  loadBoundSelection,
  updateSelectionPricesFromServiceCatalog,
} from '../utils/services.utils';
import {
  getOverlappingCategoriesForCategory,
  isBoundBasedCategory,
  isBundle,
  isJourneyBasedCategory,
  isSeatBundle,
} from '../../modules/ancillaries/utils/category.utils';
import { CommonBookingFeatureState } from '../store.interface';
import { COMMON_BOOKING_FEATURE_KEY } from '../constants';

const flightServices = (bounds: FinnairServiceBoundItem[], flightId: string): FinnairPassengerServiceItem[] => {
  if (!bounds) {
    return [];
  }
  return [
    ...bounds.reduce(
      (allServices, b) => allServices.concat(...(b.segments.find((s) => s.id === flightId)?.passengers || [])),
      []
    ),
  ];
};

const campaignIds = (catalog: ServiceCatalog | undefined): string[] =>
  (catalog?.campaigns ?? []).map(({ id }) => id).sort((a, b) => a.localeCompare(b));

const campaignsStillShown = (state: ServicesState, catalog: ServiceCatalog): string[] =>
  equals(campaignIds(state.services), campaignIds(catalog)) && Array.isArray(state.campaignsShown)
    ? state.campaignsShown
    : [];

const getInitialValue = (state: ServicesState, selection: string) =>
  Object.keys((getIn(state, selection) as object) || {}).length ? setIn(state, selection, {}) : state;

const getOnlyIncludedCheckedAndMealsIfModifiable = (cartOrOrderData: CartOrOrder | undefined, category: Category) => {
  const includedServices = cartOrOrderData?.services?.included?.find((s) => s.category === category)?.bounds ?? [];
  return includedServices.map((service) => {
    return {
      ...service,
      segments: service.segments.map((segment) => {
        return {
          ...segment,
          passengers: segment.passengers.map((passengerServiceItem) => {
            return {
              ...passengerServiceItem,
              services: passengerServiceItem.services.filter(
                (s) =>
                  SubCategory.CHECKED_BAGGAGE === s.subCategory ||
                  ([SubCategory.SPECIAL_DIET, SubCategory.MEAL].includes(s.subCategory) && s.modifiable)
              ),
            };
          }),
        };
      }),
    };
  });
};

const loadServiceSelectionsForCategoryFromCartOrOrderData = (
  initialValue: ServicesState,
  cartOrOrderData: CartOrOrder | undefined,
  category: Category,
  multi = false
) => {
  const unpaidServices = cartOrOrderData?.services?.unpaid?.find((s) => s.category === category)?.bounds ?? [];
  const includedServices = getOnlyIncludedCheckedAndMealsIfModifiable(cartOrOrderData, category);
  const unpaidAndIncludedServices = unpaidServices.concat(includedServices);
  return (
    unpaidAndIncludedServices.reduce((servicesState: ServicesState, currentBound) => {
      return currentBound.segments.reduce((boundServiceState: ServicesState, currentSegment) => {
        const servicesForFlight = flightServices(unpaidAndIncludedServices, currentSegment.id);
        const newServiceCatalogServices = servicesForFlight
          .filter((passengerServiceItem) => passengerServiceItem.services.length > 0)
          .reduce(
            (passengerServices, service) => {
              const sellableServices = service.services.filter(isNotIncludedService);
              const id = category === Category.COVER ? CustomServiceType.GROUP : service.id;
              if (multi) {
                passengerServices[id] = sellableServices.map((serviceItem) => asTravelerService(serviceItem));
              } else if (isBoundBasedCategory(category)) {
                const boundBasedPassengerServices = loadBoundSelection({ ...service, services: sellableServices });
                passengerServices[id] = ((passengerServices[id] as TravelerService[]) || []).concat(
                  boundBasedPassengerServices.filter((passengerService) => passengerService.quantity > 0)
                );
              } else if (sellableServices[0]) {
                passengerServices[id] = asTravelerService(sellableServices[0]);
              }
              return passengerServices;
            },
            {} as Record<string, TravelerService | TravelerService[]>
          );

        const getFragmentId = () => {
          if (isBoundBasedCategory(category)) {
            return currentBound.id;
          }
          if (isJourneyBasedCategory(category)) {
            return CustomServiceType.JOURNEY;
          }
          return currentSegment.id;
        };
        if (!Object.keys(newServiceCatalogServices).length) {
          return boundServiceState;
        }

        return setIn(boundServiceState, ['selections', category, getFragmentId()], newServiceCatalogServices);
      }, servicesState);
    }, initialValue) || initialValue
  );
};

export const initialState: ServicesState = Object.seal({
  status: ServicesStatus.INITIAL,
  campaignsShown: [],
});

const replaceSelection = (
  state: ServicesState,
  fragmentId: string,
  travelerId: string,
  service: keyof ServiceSelections,
  newVal?: TravelerService | TravelerService[] | undefined
) => {
  const newValIsEmpty = !newVal;
  const oldValIsEmpty = !state?.selections?.[service]?.[fragmentId]?.[travelerId];
  if (newValIsEmpty && oldValIsEmpty) {
    return state;
  }
  return {
    ...state,
    selections: {
      ...state.selections,
      [service]: {
        ...(state.selections?.[service] || {}),
        [fragmentId]: {
          ...(state.selections?.[service]?.[fragmentId] || {}),
          [travelerId]: newVal,
        },
      },
    },
  };
};

const mergeSelection = (
  current: TravelerService | TravelerService[] | undefined,
  selection?: TravelerService
): TravelerService | TravelerService[] | undefined => {
  if (Array.isArray(current) && isPresent(selection)) {
    return current.some((s) => s?.variant === selection.variant)
      ? current.map((s) => (s?.variant === selection.variant ? selection : s))
      : [...current, selection];
  }
  return selection;
};

const clearOverlappingSelections = (
  state: ServicesState,
  category: Category,
  fragmentId: string,
  travelerId: string
): ServicesState => {
  const segmentId = asSegmentId(fragmentId);
  const overlappingFragmentIds = [segmentId, `${BOUND_FRAGMENT_PREFIX}${segmentId}`];

  return getOverlappingCategoriesForCategory(category)
    .filter((c) => c !== Category.SEAT)
    .reduce(
      (selectionsForCategory, overlappingCategory) =>
        overlappingFragmentIds.reduce(
          (selectionsForFragment, fragmentIdToClear) =>
            replaceSelection(
              selectionsForFragment,
              fragmentIdToClear,
              travelerId,
              overlappingCategory as keyof ServiceSelections
            ),
          selectionsForCategory
        ),
      state
    );
};

const loadSelectionsFromCartOrOrder = (state: ServicesState, cartOrOrderData: CartOrOrder): ServicesState => {
  const supportedCategories = [
    Category.CABIN_BAGGAGE,
    Category.BAGGAGE,
    Category.MEAL,
    Category.WIFI,
    Category.LOUNGE,
    Category.COVER,
    Category.PET,
    Category.SPORT,
    Category.SAF,
    Category.PRIORITY,
    Category.SEAT_WIFI,
    Category.SEAT_CABIN_BAGGAGE,
    Category.SEAT_PRIORITY,
    Category.SEAT_MEAL,
    Category.LOUNGE_CABIN_BAGGAGE,
    Category.LOUNGE_WIFI,
    Category.LOUNGE_PRIORITY,
    Category.WIFI_MEAL,
  ];

  const withSelections = supportedCategories.reduce((currentState: ServicesState, category) => {
    const innerState = currentState || state;

    currentState = loadServiceSelectionsForCategoryFromCartOrOrderData(
      getInitialValue(innerState, `selections.${category}`),
      cartOrOrderData,
      category,
      category === Category.COVER || isSeatBundle(category)
    );

    return currentState;
  }, null as ServicesState);
  return {
    ...withSelections,
    bundleEligibilities: cartOrOrderData?.eligibilities?.serviceCatalog
      ? {
          ...cartOrOrderData.eligibilities.serviceCatalog,
          categories: cartOrOrderData.eligibilities.serviceCatalog.categories?.filter((eligCat) =>
            isSeatBundle(eligCat.category)
          ),
        }
      : undefined,
  };
};

export const servicesReducer = createReducer(
  initialState,
  on(updateStoreAction, (state, { payload, features }) => {
    if (features.includes(COMMON_BOOKING_FEATURE_KEY)) {
      return loadSelectionsFromCartOrOrder(
        state,
        (payload as BookingAppState)?.cart?.cartData ??
          (payload as CommonBookingFeatureState)?.commonBooking?.order?.orderData
      );
    }
    return state;
  }),
  on(ServicesActions.loadServiceSelectionsFromCart, CartActions.setCartData, (state, { cartData }) =>
    loadSelectionsFromCartOrOrder(state, cartData)
  ),
  on(ServicesActions.startLoading, (state) => ({ ...state, status: ServicesStatus.PENDING })),
  on(ServicesActions.error, (state) => ({ ...state, status: ServicesStatus.LOAD_ERROR })),
  on(ServicesActions.clearAllCategories, (state) => DataUtils.wrap(state).deleteIn(['selections']).value()),
  on(ServicesActions.resetCategory, (state, { category }) =>
    DataUtils.wrap(state).deleteIn(['selections', category]).value()
  ),
  on(ServicesActions.setSelectionsForFragment, (state, { category, fragmentId, selections }) => {
    const stateWithOverlapsCleared = Object.keys(selections).reduce(
      (currentState, travelerId) =>
        selections[travelerId]?.length
          ? clearOverlappingSelections(currentState, category, fragmentId, travelerId)
          : currentState,
      state
    );

    return DataUtils.wrap(stateWithOverlapsCleared)
      .updateIn(['selections', category as keyof ServiceSelections, fragmentId], () => selections)
      .value();
  }),
  on(ServicesActions.setSelectionForFragmentForTraveler, (state, { category, fragmentId, travelerId, selection }) => {
    const stateWithOverlapsCleared = clearOverlappingSelections(state, category, fragmentId, travelerId);
    const current =
      stateWithOverlapsCleared.selections?.[category as keyof ServiceSelections]?.[fragmentId]?.[travelerId];

    // Bundle: single-item array
    const updatedSelection = isBundle(category)
      ? isPresent(selection)
        ? [selection]
        : undefined
      : mergeSelection(current, selection);

    return replaceSelection(
      stateWithOverlapsCleared,
      fragmentId,
      travelerId,
      category as keyof ServiceSelections,
      updatedSelection
    );
  }),
  on(ServicesActions.setCoverForId, (state, { journeyId, travelerId, service }) =>
    DataUtils.wrap(state)
      .updateIn(['selections', Category.COVER, journeyId, travelerId], (covers) => {
        const existingCovers = (covers as TravelerService[] | undefined) ?? [];
        return [service]
          .concat(existingCovers)
          .filter(Boolean)
          .filter(uniqueBy((cover) => cover.variant));
      })
      .value()
  ),
  // new catalog has not been reported for the product optimizer test yet
  on(ServicesActions.setServiceCatalog, ({ productOptimizerTestReported: _reported, ...state }, { catalog }) => ({
    ...state,
    status: ServicesStatus.OK,
    services: catalog,
    campaignsShown: campaignsStillShown(state, catalog),
    ...(state.selections && { selections: updateSelectionPricesFromServiceCatalog(state.selections, catalog) }),
  })),
  on(ServicesActions.setServiceAvailability, (state, { serviceAvailability }) => ({
    ...state,
    serviceAvailability,
  })),
  on(ServicesActions.setCampaignShown, (state, { id }) => ({
    ...state,
    campaignsShown: (Array.isArray(state.campaignsShown) ? state.campaignsShown : []).concat(id).filter(unique),
  })),
  on(ServicesActions.setProductOptimizerTestReported, (state) => ({
    ...state,
    productOptimizerTestReported: true,
  })),
  on(ServicesActions.setUpsell, (state, upsell) => ({
    ...state,
    upsell,
  })),
  on(ServicesActions.clearUpsell, (state) => DataUtils.wrap(state).deleteIn(['upsell']).value()),
  on(routerNavigatedAction, (state, { payload }) => {
    if (
      pathIsWithinHardcodedPath(payload.routerState.url, RootPaths.BOOKING_ROOT) ||
      isInMmb(payload.routerState.url)
    ) {
      return state;
    }
    return DataUtils.wrap(state)
      .setIn('status', ServicesStatus.INITIAL)
      .deleteIn('services')
      .deleteIn('selections')
      .deleteIn('upsell')
      .deleteIn('bundleEligibilities')
      .value();
  }),
  on(CartActions.setCartData, (state, { cartData }) => {
    return loadSelectionsFromCartOrOrder({ ...state }, cartData);
  }),
  on(OrderActions.setOrderData, (state, { order }) => {
    return loadSelectionsFromCartOrOrder({ ...state }, order);
  }),
  on(ServicesActions.removeAllPendingServices, (state) => {
    return {
      ...state,
      selections: {},
    };
  }),
  on(
    ServicesActions.reset,
    // Data Clean-ups: BookingActions
    BookingActions.setTravelType, // @todo Do we need to fix this, as we moved setTravelType from the booking to the globalBooking store?
    BookingActions.selectTravelClass, // @todo Do we need to fix this, as we moved selectTravelClass from the booking to the globalBooking store?
    // Data Clean-ups: GlobalBookingActions
    GlobalBookingActions.setFlights,
    GlobalBookingActions.updateFlight,
    GlobalBookingActions.setPaxAmount,
    GlobalBookingActions.increasePaxAmountField,
    GlobalBookingActions.decreasePaxAmountField,
    // Data Clean-ups: When order created
    GlobalActions.confirmation,
    leaveError,
    // Data Clean-ups: OffersActions
    OffersActions.reset,
    OffersActions.setInboundId,
    OffersActions.setInboundFareFamily,
    OffersActions.setOutboundId,
    OffersActions.setOutboundFareFamily,
    () => ({ ...initialState })
  )
);
